import { forwardRef, InputHTMLAttributes, ReactNode, useId } from "react";
import s from "./Choice.module.scss";

/**
 * `variant="card"` (2F, boards 50/51 `.ch`): the whole card is the label around a real input, 48px or taller; the
 * chosen card gets a primary border and tint. The input is named by `label` alone and described by `detail` (a second
 * line) and `trailing` (e.g. a pill at the end). Radio cards with one `name` inside a `RadioGroup` are one native group,
 * so the browser's arrow keys move between them. Without a variant: today's plain row, unchanged.
 */
type ChoiceProps = {
  label: ReactNode;
  description?: string;
  variant?: "row" | "card";
  detail?: ReactNode;
  trailing?: ReactNode;
} & InputHTMLAttributes<HTMLInputElement>;

const make = (type: "checkbox" | "radio", role?: "switch") => {
  const C = forwardRef<HTMLInputElement, ChoiceProps>(({ label, description, variant = "row", detail, trailing, className, ...rest }, ref) => {
    const id = useId();
    if (variant === "card" && !role) {
      const described = [detail != null && `${id}-d`, trailing != null && `${id}-t`].filter(Boolean).join(" ") || undefined;
      const card = [s.card, rest.checked ? s.on : "", rest.disabled ? s.off : "", className ?? ""].join(" ").replace(/\s+/g, " ").trim();
      return (
        <label className={card}>
          <input ref={ref} type={type} aria-labelledby={`${id}-l`} aria-describedby={described} className={s[type]} {...rest} />
          <span className={s.text}>
            <span id={`${id}-l`} className={s.title}>{label}</span>
            {detail != null && <span id={`${id}-d`} className={s.desc}>{detail}</span>}
          </span>
          {trailing != null && <span id={`${id}-t`} className={s.trailing}>{trailing}</span>}
        </label>
      );
    }
    return (
      <label className={[s.row, role ? s.switchRow : "", className ?? ""].join(" ").trim()}>
        <input ref={ref} type={type} role={role} aria-describedby={description ? `${id}-d` : undefined} className={role ? s.switch : s[type]} {...rest} />
        <span className={s.text}><span>{label}</span>{description && <span id={`${id}-d`} className={s.desc}>{description}</span>}</span>
      </label>
    );
  });
  C.displayName = role ? "Switch" : type === "radio" ? "Radio" : "Checkbox";
  return C;
};

export const Checkbox = make("checkbox");
export const Radio = make("radio");
export const Switch = make("checkbox", "switch");

export const RadioGroup = ({ label, children }: { label: string; children: ReactNode }) => (
  <div role="radiogroup" aria-label={label} className={s.group}>{children}</div>
);
